import React, { ReactNode, useEffect } from 'react'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next/pages'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FAVICON_LINK_KEYS } from 'shared/constants'
import useUser from 'shared/hooks/use-user'
import { DomainAssetsInterface } from 'shared/types/domain'
import { PUBLIC_LAYOUT_X_PADDING } from './constants/public-layout-constants'
import PublicFooter from './public-footer'
import PublicHeader from './public-header'

const PUBLIC_LAYOUT_CLASSNAMES = 'bg-bg-page font-ubuntu text-text-tertiary'

interface Props extends React.PropsWithChildren {
  pageTitle: ParseKeys
  domainAssets?: DomainAssetsInterface
}

function PublicLayout({ children, pageTitle, domainAssets }: Props): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const { pathname, asPath, replace } = useRouter()

  useEffect(() => {
    if (!user) return
    replace(pathname, asPath, {
      locale: user.dashboardLocale,
    })
  }, [asPath, pathname, user?.dashboardLocale])

  useEffect(() => {
    document.body.className = PUBLIC_LAYOUT_CLASSNAMES
  }, [])

  return (
    <>
      <Head>
        <title>{t('public.templates.templates_library')}</title>
        {domainAssets?.faviconUrl && (
          <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={domainAssets.faviconUrl} />
        )}
      </Head>
      <div className="bg-bg-primary">
        <PublicHeader title={pageTitle} />
        <div className="overflow-hidden">
          <div className={PUBLIC_LAYOUT_X_PADDING}>
            <main className="min-w-0 pt-11 pb-20 lg:pb-24">{children}</main>
          </div>
        </div>
        <PublicFooter />
      </div>
    </>
  )
}

export const getPublicLayout = (
  page: ReactNode,
  props: { pageTitle: ParseKeys; domainAssets?: DomainAssetsInterface },
): ReactNode => (
  <PublicLayout pageTitle={props.pageTitle} domainAssets={props.domainAssets}>
    {page}
  </PublicLayout>
)
